import React, { useId } from "react";
import PropTypes from "prop-types";
import { Button, Box, Typography, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";

const AnimatedAIIcon = styled(Box)({
  marginRight: "8px",
  width: "24px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #2A65F8, #7A5AF1)",
  color: "white",
  padding: "6px 16px",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: "bold",
  fontSize: "0.75rem",
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
  transition: "all 0.3s ease-in-out",
  "& .MuiTypography-root": {
    color: "white",
  },
  "&:hover": {
    boxShadow: "0 0 25px rgba(167, 139, 250, 0.9)",
    transform: "translateY(-2px)",
    "& .MuiTypography-root": {
      color: "white",
    },
  },
  "&:active": {
    "& .MuiTypography-root": {
      color: "white",
    },
  },
}));

function AnimatedCatchupIcon({ gradientId }) {
  const gid = gradientId.replace(/:/g, "");
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" width="24" height="24" aria-hidden>
      <defs>
        <linearGradient id={`iconGradient-${gid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#A78BFA", stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: "#FFFFFF", stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <g>
        <g>
          <line x1="50" y1="50" x2="50" y2="15" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="2.5" />
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="8s" repeatCount="indefinite" />
        </g>
        <g>
          <line x1="50" y1="50" x2="20" y2="35" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="2.5" />
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="-360 50 50" dur="10s" repeatCount="indefinite" />
        </g>
        <g>
          <line x1="50" y1="50" x2="80" y2="65" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="2.5" />
          <animateTransform attributeName="transform" type="rotate" from="360 50 50" to="0 50 50" dur="6s" repeatCount="indefinite" />
        </g>
        <circle cx="50" cy="50" r="18" fill={`url(#iconGradient-${gid})`} />
        <g>
          <circle cx="50" cy="15" r="10" fill={`url(#iconGradient-${gid})`} />
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="8s" repeatCount="indefinite" />
        </g>
        <g>
          <circle cx="20" cy="35" r="8" fill={`url(#iconGradient-${gid})`} />
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="-360 50 50" dur="10s" repeatCount="indefinite" />
        </g>
        <g>
          <circle cx="80" cy="65" r="9" fill={`url(#iconGradient-${gid})`} />
          <animateTransform attributeName="transform" type="rotate" from="360 50 50" to="0 50 50" dur="6s" repeatCount="indefinite" />
        </g>
      </g>
    </svg>
  );
}

function AIActionButton({ text, onClick, iconOnly }) {
  const gradientId = useId();

  if (iconOnly) {
    return (
      <Tooltip title={text} enterDelay={400}>
        <IconButton
          onClick={onClick}
          size="small"
          aria-label={text}
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            p: 0,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #2A65F8, #7A5AF1)",
            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
            "&:hover": {
              boxShadow: "0 0 20px rgba(167, 139, 250, 0.75)",
              background: "linear-gradient(135deg, #3568f5, #8468f0)",
            },
          }}
        >
          <AnimatedCatchupIcon gradientId={gradientId} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <StyledButton onClick={onClick}>
      <AnimatedAIIcon>
        <AnimatedCatchupIcon gradientId={gradientId} />
      </AnimatedAIIcon>
      <Typography variant="button" fontWeight="regular" sx={{ lineHeight: 1, textTransform: "none", fontSize: "inherit", color: "white" }}>
        {text}
      </Typography>
    </StyledButton>
  );
}

AIActionButton.defaultProps = {
  iconOnly: false,
};

AIActionButton.propTypes = {
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  iconOnly: PropTypes.bool,
};

export default AIActionButton;
